import { useEffect, useState } from "react";
import axios from "axios";
import { SEARCH_DEBOUNCE_MS } from "../servicesManageConstants";

/**
 * Fetches the last 10 services (sorted by created_at DESC server-side).
 * Used as the default view when no search query is active.
 * @param {Object} args - Fetch arguments
 * @returns {Promise<void>}
 */
async function fetchLast({ signal, setItems, setError, setLoading }) {
    setLoading(true);
    setError(null);
    try {
        const { data } = await axios.get("/admin/services/last");
        if (!signal.aborted) setItems(Array.isArray(data) ? data : []);
    } catch (err) {
        if (!signal.aborted) {
            const msg = err?.response?.data;
            setError(typeof msg === "string" ? msg : "Impossible de charger les services");
        }
    } finally {
        if (!signal.aborted) setLoading(false);
    }
}

/**
 * Fetches up to 10 services matching the given substring (case-insensitive).
 * @param {Object} args - Fetch arguments
 * @returns {Promise<void>}
 */
async function fetchSearch({ term, signal, setItems, setError, setLoading }) {
    setLoading(true);
    setError(null);
    try {
        const { data } = await axios.get(`/admin/services/search/${encodeURIComponent(term)}`);
        if (!signal.aborted) setItems(Array.isArray(data) ? data : []);
    } catch (err) {
        if (!signal.aborted) {
            const msg = err?.response?.data;
            setError(typeof msg === "string" ? msg : "Impossible d'effectuer la recherche");
        }
    } finally {
        if (!signal.aborted) setLoading(false);
    }
}

/**
 * Hook that owns the services list state for the services admin view.
 * Switches between /last (no query) and /search (debounced 300ms) automatically,
 * and exposes setItems so the parent can apply optimistic updates on toggles.
 * @param {string} query - Current search query
 * @returns {{ items: Array, setItems: Function, loading: boolean, error: string|null, active: boolean }}
 */
function useServicesData(query) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const trimmed = (query || "").trim();
    const active = trimmed.length > 0;

    useEffect(() => {
        const controller = new AbortController();
        const handle = setTimeout(() => {
            const args = { signal: controller.signal, setItems, setError, setLoading };
            if (active) fetchSearch({ term: trimmed, ...args });
            else fetchLast(args);
        }, active ? SEARCH_DEBOUNCE_MS : 0);
        return () => { clearTimeout(handle); controller.abort(); };
    }, [trimmed, active]);

    return { items, setItems, loading, error, active };
}

export default useServicesData;
